import type { BackgroundValue, StyleBackgroundLayer } from './types';

const stopsToValue = (stops: { color: string; at: number }[]): string =>
  stops
    .slice()
    .sort((a, b) => a.at - b.at)
    .map((stop) => `${stop.color} ${stop.at}%`)
    .join(', ');

const layerToImage = (layer: StyleBackgroundLayer): string => {
  switch (layer.kind) {
    case 'color':
      return `linear-gradient(${layer.color}, ${layer.color})`;
    case 'image':
      return `url("${layer.url}")`;
    case 'linear':
      return `linear-gradient(${layer.angle}deg, ${stopsToValue(layer.stops)})`;
    case 'radial':
      return `radial-gradient(${layer.shape} at ${layer.position ?? 'center'}, ${stopsToValue(layer.stops)})`;
  }
};

const sizeOf = (layer: StyleBackgroundLayer): string =>
  layer.kind === 'image' ? layer.size ?? 'cover' : 'auto';

const positionOf = (layer: StyleBackgroundLayer): string =>
  layer.kind === 'image' ? layer.position ?? 'center' : 'center';

const repeatOf = (layer: StyleBackgroundLayer): string =>
  layer.kind === 'image' ? layer.repeat ?? 'no-repeat' : 'no-repeat';

const isPaintable = (layer: StyleBackgroundLayer): boolean =>
  layer.kind === 'color' ? layer.color !== '' : layer.kind !== 'image' || layer.url !== '';

export function backgroundValueToCss(value: BackgroundValue): string {
  const parts: string[] = [];

  if (value.color) {
    parts.push(`background-color: ${value.color}`);
  }

  const layers = (value.layers ?? []).filter(isPaintable);

  if (layers.length > 0) {
    parts.push(`background-image: ${layers.map(layerToImage).join(', ')}`);
    parts.push(`background-size: ${layers.map(sizeOf).join(', ')}`);
    parts.push(`background-position: ${layers.map(positionOf).join(', ')}`);
    parts.push(`background-repeat: ${layers.map(repeatOf).join(', ')}`);
  }

  return parts.join('; ');
}
